<script lang="ts">
  import { onMount } from 'svelte'
  import { api, type MeResponse } from './lib/api'
  import Toasts from './components/Toasts.svelte'
  import Dashboard from './views/Dashboard.svelte'
  import Domains from './views/Domains.svelte'
  import Categories from './views/Categories.svelte'
  import Mikrotik from './views/Mikrotik.svelte'
  import Happ from './views/Happ.svelte'
  import Targets from './views/Targets.svelte'
  import Panels from './views/Panels.svelte'
  import Settings from './views/Settings.svelte'
  import Audit from './views/Audit.svelte'

  let me = $state<MeResponse | null>(null)
  let loading = $state(true)
  let tab = $state<'dashboard'|'domains'|'categories'|'mikrotik'|'happ'|'targets'|'panels'|'settings'|'audit'>('dashboard')
  let menuOpen = $state(false)

  onMount(async () => {
    try {
      me = await api.me()
    } catch (e) {
      me = { authenticated: false }
    } finally {
      loading = false
    }
  })

  const tabs = [
    { id: 'dashboard', label: 'Обзор' },
    { id: 'domains', label: 'Домены' },
    { id: 'categories', label: 'Категории' },
    { id: 'mikrotik', label: 'MikroTik IP' },
    { id: 'happ', label: 'Happ правила' },
    { id: 'targets', label: 'Эндпоинты' },
    { id: 'panels', label: 'Панели' },
    { id: 'settings', label: 'Настройки' },
    { id: 'audit', label: 'Журнал' }
  ] as const
</script>

<Toasts />

{#if loading}
  <div class="min-h-screen flex items-center justify-center text-muted">Загрузка...</div>
{:else if !me?.authenticated}
  <div class="min-h-screen flex flex-col items-center justify-center gap-4 p-8 text-center">
    <h1 class="text-2xl font-semibold">VLESS Routing Config</h1>
    <p class="text-muted">Управление доменами и маршрутизацией для VPN</p>
    <a href="/auth/login" class="btn btn-primary mt-2">Войти через GitLab</a>
  </div>
{:else if !me.allowed}
  <div class="min-h-screen flex flex-col items-center justify-center gap-4 p-8 text-center">
    <h1 class="text-2xl font-semibold text-accent-red">Доступ запрещён</h1>
    <p class="text-muted">Пользователь <b>{me.username}</b> не в списке allowed.</p>
    <a href="/auth/logout" class="btn btn-secondary">Выйти</a>
  </div>
{:else}
  <div class="min-h-screen">
    <header class="sticky top-0 z-20 bg-bg-card/95 backdrop-blur border-b border-bg-border px-4 sm:px-6 py-3 flex items-center justify-between gap-3">
      <div class="flex items-center gap-2 sm:gap-3 min-w-0">
        <div class="w-8 h-8 rounded-lg bg-accent flex items-center justify-center text-white font-bold text-sm flex-shrink-0">V</div>
        <h1 class="text-sm sm:text-base font-semibold truncate">VLESS Routing</h1>
      </div>
      <div class="flex items-center gap-2 sm:gap-4 text-sm">
        <span class="text-muted hidden md:inline">
          {me.name} <span class="text-slate-500">(@{me.username})</span>
          <span class="badge badge-{me.role==='admin'?'green':me.role==='editor'?'blue':'neutral'} ml-2">{me.role}</span>
        </span>
        <span class="md:hidden">
          <span class="badge badge-{me.role==='admin'?'green':me.role==='editor'?'blue':'neutral'}">{me.username}</span>
        </span>
        <a href="/auth/logout" class="btn btn-ghost text-xs sm:text-sm px-2 sm:px-3" title="Выйти">↩</a>
      </div>
    </header>

    <!-- Desktop nav -->
    <nav class="hidden md:block px-6 pt-4">
      <div class="inline-flex gap-1 bg-bg-card border border-bg-border rounded-lg p-1">
        {#each tabs as t}
          <button class="tab {tab===t.id?'tab-active':''}" onclick={() => (tab = t.id)}>{t.label}</button>
        {/each}
      </div>
    </nav>

    <!-- Mobile nav — hamburger -->
    <nav class="md:hidden px-4 pt-3">
      <button class="w-full flex items-center justify-between bg-bg-card border border-bg-border rounded-lg px-4 py-2.5 text-sm"
              onclick={() => (menuOpen = !menuOpen)}>
        <span class="font-medium">{tabs.find(t => t.id === tab)?.label}</span>
        <span class="text-muted transition-transform {menuOpen ? 'rotate-180' : ''}">▾</span>
      </button>
      {#if menuOpen}
        <div class="mt-1 bg-bg-card border border-bg-border rounded-lg overflow-hidden">
          {#each tabs as t}
            <button class="block w-full text-left px-4 py-2.5 text-sm hover:bg-bg-hover {tab===t.id?'bg-accent/20 text-accent':''}"
                    onclick={() => { tab = t.id; menuOpen = false }}>{t.label}</button>
          {/each}
        </div>
      {/if}
    </nav>

    <main class="p-4 sm:p-6 max-w-[1400px] mx-auto">
      {#if tab === 'dashboard'}<Dashboard />{/if}
      {#if tab === 'domains'}<Domains />{/if}
      {#if tab === 'categories'}<Categories />{/if}
      {#if tab === 'mikrotik'}<Mikrotik />{/if}
      {#if tab === 'happ'}<Happ />{/if}
      {#if tab === 'targets'}<Targets />{/if}
      {#if tab === 'panels'}<Panels />{/if}
      {#if tab === 'settings'}<Settings />{/if}
      {#if tab === 'audit'}<Audit />{/if}
    </main>
  </div>
{/if}
